import { type FC, useCallback } from 'react';
import pageLinks from 'dibs-pagelinks';
import classnames from 'classnames';
import styles from './ReviewPagination.scss';
import { trackEvent } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';

import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft';

const TOTAL_PAGE_LINKS = 6;

type ReviewPaginationProps = {
    currentPage: number;
    numberOfPages: number;
    onChangePage: (pageNumber: number) => void;
};

export const ReviewPagination: FC<ReviewPaginationProps> = ({
    currentPage,
    numberOfPages,
    onChangePage,
}) => {
    const onPageClick = useCallback(
        (pageNumber: number) => {
            trackEvent({
                category: 'seller reviews',
                action: 'pagination clicked',
                label: `[${pageNumber}/${numberOfPages}]`,
            });
            onChangePage(pageNumber);
        },
        [onChangePage, numberOfPages]
    );

    const pageLinksInfo = pageLinks(numberOfPages, currentPage, TOTAL_PAGE_LINKS);

    const paginationItems = pageLinksInfo.map(
        ({
            type,
            key,
            pageNumber,
            isSelected,
        }: {
            type: 'back' | 'forward' | 'page' | 'ellipsis';
            key: string;
            pageNumber: number;
            isSelected: boolean;
        }) => {
            let paginationItem = null;
            // istanbul ignore else
            if (type === 'back' || type === 'forward') {
                //arrow
                let ArrowIcon = ArrowRight;
                let pageToGoTo = currentPage + 1;
                if (type === 'back') {
                    ArrowIcon = ArrowLeft;
                    pageToGoTo = currentPage - 1;
                }
                paginationItem = (
                    <Link
                        className={classnames(styles.button, styles.clickable)}
                        onClick={() => onPageClick(pageToGoTo)}
                        key={key}
                    >
                        <ArrowIcon className={styles.arrowIcon} />
                    </Link>
                );
            } else if (type === 'page') {
                //pagination item
                paginationItem = (
                    <Link
                        className={classnames(styles.button, styles.clickable, {
                            [styles.selected]: isSelected,
                        })}
                        onClick={() => onPageClick(pageNumber)}
                        key={key}
                    >
                        {pageNumber}
                    </Link>
                );
            } else if (type === 'ellipsis') {
                //elipses
                paginationItem = (
                    <div className={styles.elipses} key={key}>
                        {'...'}
                    </div>
                );
            }
            return paginationItem;
        }
    );

    return <div className={styles.wrapper}>{paginationItems}</div>;
};
